import {
  Navigate,
  RouterProvider,
  ScrollRestoration,
  createBrowserRouter,
  useLocation,
} from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";

import NotFound from "screens/NotFound";
import LoginPage from "screens/Login";
import Layout from "components/Layout";
import Dashboard from "screens/Dashboard";
import SelectOption from "screens/SelectOption";
import FaceScan from "screens/FaceScan";
import FingerScan from "screens/FingerScan";
import Result from "screens/ScanResult";
import UpdatePatientInfo from "screens/UpdatePatientInfo";
import UpdateObservations from "screens/UpdateObservations";
import History from "screens/History";

const RequireAuth = ({ children }) => {
  const location = useLocation();
  try {
    const expiresAt = jwtDecode(localStorage.getItem("token")).exp * 1000;
    if (expiresAt > Date.now()) return children;
    else throw new Error("Token Expired.");
  } catch (err) {
    console.error(err);
    localStorage.removeItem("emp_id");
    localStorage.removeItem("client_code");
    localStorage.removeItem("token");
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <RequireAuth>
        <ScrollRestoration />
        <Layout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Dashboard /> },
      { path: "select-option", element: <SelectOption /> },
      { path: "result", element: <Result /> },
      { path: "history", element: <History /> },
      {
        path: "update",
        children: [
          { path: "patient-info/:pID", element: <UpdatePatientInfo /> },
          { path: "observations/:scanID", element: <UpdateObservations /> },
        ],
      },
    ],
  },
  {
    path: "face-scan",
    element: (
      <RequireAuth>
        <FaceScan />
      </RequireAuth>
    ),
  },
  {
    path: "finger-scan",
    element: (
      <RequireAuth>
        <FingerScan />
      </RequireAuth>
    ),
  },
  { path: "login", element: <LoginPage /> },
  { path: "*", element: <NotFound /> },
]);

const App = () => (
  <>
    <RouterProvider router={router} />
    <div className="fixed top-0 bottom-0 left-0 right-0 z-50 h-screen w-screen hidden lg:flex landscape:flex flex-col items-center justify-center bg-white text-darkgray">
      <svg className="shrink-0 h-32 w-36" fill="currentColor" viewBox="0 0 463.708 463.707">
        <path d="M432.771,351.013v-28.2c0-1.396-1.118-2.524-2.515-2.524c-1.387,0-2.505,1.119-2.505,2.524v28.2c0,1.387,1.118,2.506,2.505,2.506C431.653,353.519,432.771,352.399,432.771,351.013z" />
        <path d="M229.569,231.816c-5.279,0-9.562,2.248-9.562,5.01c0,2.773,4.283,5.012,9.562,5.012h198.985c8.606,0,15.625,6.999,15.625,15.615v158.881c0,8.626-7.009,15.635-15.625,15.635H229.262c-5.278,0-9.562-2.228-9.562-4.973c0-2.744,4.284-4.973,9.562-4.973h180.712c1.387,0,2.516-1.118,2.516-2.505v-165.25c0-1.387-1.119-2.505-2.516-2.505H229.262c-5.278,0-9.562-4.284-9.562-9.562V111.329c0-14.133-11.504-25.646-25.656-25.646H35.163c-14.152,0-25.656,11.503-25.656,25.646v326.712c0,14.143,11.504,25.666,25.656,25.666h158.881c9.429,0,17.644-5.136,22.07-12.747c2.658-4.562,7.459-8.96,12.737-8.96h199.693c14.144,0,25.656-11.504,25.656-25.666V257.463c0-14.144-11.503-25.647-25.656-25.647H229.569z M229.262,256.784h168.654c5.278,0,9.562,4.283,9.562,9.562V407.47c0,5.278-4.284,9.562-9.562,9.562H229.262c-5.278,0-9.562-4.284-9.562-9.562V266.347C219.7,261.067,223.974,256.784,229.262,256.784z M209.668,438.051c0,8.606-6.999,15.615-15.625,15.615H35.163c-8.606,0-15.625-7-15.625-15.615V111.329c0-8.606,7.009-15.616,15.625-15.616h158.881c8.616,0,15.625,7.01,15.625,15.616V438.051z" />
        <path d="M197.228,127.394H31.969c-1.386,0-2.505,1.119-2.505,2.505v286.818c0,1.377,1.119,2.524,2.505,2.524h165.259c1.396,0,2.515-1.138,2.515-2.524V129.899C199.743,128.513,198.625,127.394,197.228,127.394z M185.16,414.211H44.046c-5.278,0-9.562-4.284-9.562-9.562V141.986c0-5.278,4.284-9.562,9.562-9.562H185.16c5.279,0,9.562,4.284,9.562,9.562v262.672C194.722,409.937,190.448,414.211,185.16,414.211z" />
        <path d="M100.503,112.122h28.2c1.396,0,2.515-1.119,2.515-2.505c0-1.387-1.119-2.506-2.515-2.506h-28.2c-1.396,0-2.515,1.119-2.515,2.506C97.988,110.994,99.107,112.122,100.503,112.122z" />
        <path d="M129.047,424.969h-28.888c-5.929,0-10.758,4.82-10.758,10.739v1.74c0,5.919,4.829,10.758,10.758,10.758h28.888c5.929,0,10.758-4.839,10.758-10.758v-1.74C139.805,429.788,134.976,424.969,129.047,424.969z M134.794,437.448c0,3.155-2.572,5.737-5.747,5.737h-28.888c-3.156,0-5.747-2.582-5.747-5.737v-1.74c0-3.156,2.591-5.737,5.747-5.737h28.888c3.175,0,5.747,2.581,5.747,5.737V437.448z" />
        <path d="M313.241,131.745c-0.058,0.449,2.122,1.1,4.867,1.454c2.764,0.334,5.029,0.239,5.087-0.201c0.058-0.468,0.105-0.841,0.105-0.841c3.643-29.251-3.815-58.666-20.99-82.831c-9.084-12.775-20.444-23.438-33.717-31.661c-44.781-27.808-102.109-22.147-140.55,12.498c-2.754,2.467-7.21,2.783-10.136,0.526l-8.291-6.407c-2.936-2.257-4.924-1.119-4.446,2.543l3.423,26.574c0.478,3.682,3.806,6.254,7.478,5.786l26.574-3.414c3.663-0.449,4.265-2.668,1.349-4.915l-7.497-5.805c-2.926-2.257-3.175-6.215-0.392-8.645c35.142-30.59,86.77-35.314,127.219-10.231c12.145,7.544,22.511,17.298,30.83,28.984c15.721,22.108,22.538,49.008,19.221,75.773L313.241,131.745z" />
      </svg>
      <p className="mt-6 w-72 text-lg text-center">
        Please rotate your phone to access the application.
      </p>
    </div>
    <ToastContainer />
  </>
);

export default App;
